import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatRelative, ApiError, type ResourceGroup } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  Button, Card, Modal, Field, Input, Select, Alert, Spinner, Empty, SecretReveal,
} from '../components/ui';
import { AccessModal, type AccessMatrix } from '../components/AccessModal';

interface AdminUser {
  id: string; email: string; name: string; is_active: number; totp_enabled: number;
  must_change_password: number; last_login_at: string | null; locked_until: string | null;
  groups: string | null;
}

interface AuditEntry {
  id: number; at: string; user_email: string | null; action: string;
  target_type: string | null; target_id: string | null; result: string;
  detail: Record<string, unknown>;
}

type Tab = 'users' | 'groups' | 'audit';

export function Admin() {
  const [tab, setTab] = useState<Tab>('users');

  // The super admin's page (the only account with user:read and audit:read).
  const tabs: { id: Tab; label: string }[] = [
    { id: 'users', label: 'Users and access' },
    { id: 'groups', label: 'Groups' },
    { id: 'audit', label: 'Audit log' },
  ];

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Administration</h1>

      <div className="flex gap-1 overflow-x-auto border-b border-grid [scrollbar-width:none]">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-sm transition-colors ${
              tab === t.id
                ? 'border-b-2 border-series-1 text-ink'
                : 'text-ink-secondary hover:text-ink'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'users' && <Users />}
      {tab === 'groups' && <Groups />}
      {tab === 'audit' && <AuditLog />}
    </div>
  );
}

// ===========================================================================

function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [matrix, setMatrix] = useState<AccessMatrix | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState<{ email: string; password: string } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [form, setForm] = useState({ email: '', name: '' });

  async function load() {
    try {
      const [u, m] = await Promise.all([
        api.get<{ users: AdminUser[] }>('/admin/users'),
        api.get<AccessMatrix>('/admin/access'),
      ]);
      setUsers(u.users);
      setMatrix(m);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the users.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  if (loading) return <Spinner />;

  const access = new Map((matrix?.users ?? []).map((u) => [u.id, u]));
  const kindLabel = new Map((matrix?.kinds ?? []).map((k) => [k.id, k.label]));
  /** One line saying what someone can reach. */
  const summary = (id: string) => {
    const a = access.get(id);
    if (!a) return '';
    if (a.isAdmin) return 'Everything (super admin)';
    if (!a.grants.length && !a.caps.length) return 'Nothing yet';
    const byKind = new Map<string, string[]>();
    for (const g of a.grants) {
      const list = byKind.get(g.kind) ?? [];
      list.push(g.id === '*' ? `all (${g.level})` : g.level);
      byKind.set(g.kind, list);
    }
    const parts = [...byKind.entries()].map(([k, list]) => {
      const all = list.find((x) => x.startsWith('all'));
      return `${kindLabel.get(k) ?? k}: ${all ?? `${list.length}`}`;
    });
    if (a.caps.length) parts.push(`may make ${a.caps.map((c) => c.slice(7)).join(', ')}s`);
    return parts.join(' · ');
  };
  const selectable = users.filter((u) => !access.get(u.id)?.isAdmin);
  const chosen = (matrix?.users ?? []).filter((u) => selected.has(u.id));

  return (
    <div className="space-y-3">
      {error && <Alert tone="critical">{error}</Alert>}
      {saved && <Alert tone="good">{saved}</Alert>}

      {/* Shown once. There is deliberately no way to retrieve it later. */}
      {newPassword && (
        <Alert tone="good" title={`Account created for ${newPassword.email}`}>
          <div className="mt-2 space-y-2">
            <div>Give them this password. It is shown once and cannot be recovered. They can reach nothing until you give them access.</div>
            <SecretReveal value={newPassword.password} label="temporary password" />
          </div>
        </Alert>
      )}

      <Card
        title={`${users.length} users`}
        action={
          <div className="flex gap-2">
            <Button
              variant="primary"
              disabled={!selected.size || !matrix}
              title="Tick one or more users first"
              onClick={() => { setSaved(null); setEditing(true); }}
            >
              Edit access{selected.size ? ` (${selected.size})` : ''}
            </Button>
            <Button onClick={() => setCreating(true)}>Add user</Button>
          </div>
        }
      >
        <div className="border-b border-grid px-4 py-2 text-xs text-ink-muted">
          Nobody can reach anything until given it. Tick users and press Edit access to give or take away access to
          machines, encoders, flows, devices, Magewell, intercom channels, recordings, costs and templates, one by one or all
          of a kind. Changes made for several users at once apply to each of them.
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-grid text-left text-xs text-ink-muted">
                <th className="w-8 px-4 py-2">
                  <input
                    type="checkbox"
                    className="accent-series-1"
                    aria-label="Tick everyone"
                    checked={selectable.length > 0 && selectable.every((u) => selected.has(u.id))}
                    onChange={(e) => setSelected(e.target.checked ? new Set(selectable.map((u) => u.id)) : new Set())}
                  />
                </th>
                <th className="px-2 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Can reach</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Last sign-in</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const admin = !!access.get(u.id)?.isAdmin;
                return (
                  <tr key={u.id} className={`border-b border-grid last:border-0 ${selected.has(u.id) ? 'bg-raised/40' : ''}`}>
                    <td className="px-4 py-2.5">
                      <input
                        type="checkbox"
                        className="accent-series-1"
                        disabled={admin}
                        title={admin ? 'The super admin has everything' : undefined}
                        checked={selected.has(u.id)}
                        onChange={(e) => setSelected((s) => { const n = new Set(s); if (e.target.checked) n.add(u.id); else n.delete(u.id); return n; })}
                      />
                    </td>
                    <td className="px-2 py-2.5">
                      <div className="font-medium">{u.name}</div>
                      <div className="text-xs text-ink-muted">{u.email}</div>
                    </td>
                    <td className="max-w-[28rem] px-4 py-2.5 text-xs text-ink-secondary">
                      {admin ? <Tag tone="good">super admin</Tag> : (
                        <button
                          type="button"
                          className="rounded px-1 text-left hover:bg-raised hover:text-ink"
                          title="Edit this user's access"
                          onClick={() => { setSelected(new Set([u.id])); setSaved(null); setEditing(true); }}
                        >
                          {summary(u.id)} <span className="text-ink-muted">✎</span>
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      <div className="flex flex-wrap gap-1.5">
                        {!u.is_active && <Tag tone="critical">disabled</Tag>}
                        {u.locked_until && <Tag tone="warning">locked</Tag>}
                        {u.totp_enabled === 1 && <Tag tone="good">2FA</Tag>}
                        {u.must_change_password === 1 && <Tag>must change password</Tag>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-ink-muted">
                      {u.last_login_at ? formatRelative(u.last_login_at) : 'never'}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex justify-end gap-1">
                        {/* Resetting your OWN password here would generate a
                            random one and sign you out - which is not what
                            anyone clicking it wants. Send yourself to the
                            account page instead, where you choose the password. */}
                        {u.id === me?.id ? (
                          <Link to="/account">
                            <Button variant="ghost">Change my password</Button>
                          </Link>
                        ) : (
                          <Button
                            variant="ghost"
                            onClick={async () => {
                              if (
                                !window.confirm(
                                  `Generate a new random password for ${u.email}?\n\n` +
                                    `This signs them out of every device immediately. ` +
                                    `Use it when someone is locked out, not to change a password you know.`,
                                )
                              ) {
                                return;
                              }
                              try {
                                const r = await api.post<{ temporaryPassword: string }>(
                                  `/admin/users/${u.id}/reset-password`,
                                );
                                setNewPassword({ email: u.email, password: r.temporaryPassword });
                              } catch (err) {
                                setError(err instanceof ApiError ? err.message : 'Reset failed.');
                              }
                            }}
                          >
                            Reset password
                          </Button>
                        )}
                        {!admin && (
                          <Button
                            variant="ghost"
                            onClick={async () => {
                              try {
                                await api.patch(`/admin/users/${u.id}`, { isActive: !u.is_active });
                                await load();
                              } catch (err) {
                                setError(err instanceof ApiError ? err.message : 'Update failed.');
                              }
                            }}
                          >
                            {u.is_active ? 'Disable' : 'Enable'}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {editing && matrix && chosen.length > 0 && (
        <AccessModal
          matrix={matrix}
          users={chosen}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            setSaved(`Access saved for ${chosen.map((u) => u.name).join(', ')}. It applies from their next click.`);
            void load();
          }}
        />
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="Add user"
        footer={
          <>
            <Button onClick={() => setCreating(false)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!form.email || !form.name}
              onClick={async () => {
                try {
                  const r = await api.post<{ temporaryPassword?: string; id: string }>('/admin/users', {
                    email: form.email,
                    name: form.name,
                  });
                  if (r.temporaryPassword) {
                    setNewPassword({ email: form.email, password: r.temporaryPassword });
                  }
                  setCreating(false);
                  setForm({ email: '', name: '' });
                  await load();
                  // Straight on to what they may reach.
                  if (r.id) setSelected(new Set([r.id]));
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : 'Could not create the user.');
                }
              }}
            >
              Create
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label="Name">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
          </Field>
          <Field label="Email">
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Alert tone="info">
            A strong password is generated and shown to you once; they change it at first sign-in. They can reach
            nothing until you give them access (they are ticked for you after creating).
          </Alert>
        </div>
      </Modal>
    </div>
  );
}

// ===========================================================================

// ===========================================================================

function Groups() {
  const { can } = useAuth();
  const [groups, setGroups] = useState<ResourceGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', description: '', colour: '#3987e5' });

  async function load() {
    setGroups((await api.get<{ groups: ResourceGroup[] }>('/admin/groups')).groups);
    setLoading(false);
  }

  useEffect(() => { void load(); }, []);
  if (loading) return <Spinner />;

  return (
    <div className="space-y-3">
      {error && <Alert tone="critical">{error}</Alert>}

      <Card
        title="Resource groups"
        action={can('group:manage') && <Button variant="primary" onClick={() => setCreating(true)}>Add group</Button>}
      >
        {groups.length === 0 ? (
          <Empty title="No groups">Groups scope who can control which instances.</Empty>
        ) : (
          <div className="divide-y divide-grid">
            {groups.map((g) => (
              <div key={g.id} className="flex items-center gap-3 px-4 py-3">
                <span className="h-3 w-3 shrink-0 rounded" style={{ background: g.colour }} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">{g.name}</div>
                  <div className="text-xs text-ink-muted">{g.description}</div>
                </div>
                <div className="text-xs text-ink-secondary">
                  {g.instance_count} instances · {g.user_count} users
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="Add group"
        footer={
          <>
            <Button onClick={() => setCreating(false)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!form.name}
              onClick={async () => {
                try {
                  await api.post('/admin/groups', form);
                  setCreating(false);
                  setForm({ name: '', description: '', colour: '#3987e5' });
                  await load();
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : 'Could not create the group.');
                }
              }}
            >
              Create
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label="Name" hint="Becomes an AWS tag value, so keep it stable — cost reports group on it.">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
          </Field>
          <Field label="Description">
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          <Field label="Colour">
            <input
              type="color"
              value={form.colour}
              onChange={(e) => setForm({ ...form, colour: e.target.value })}
              className="h-9 w-20 rounded bg-transparent"
            />
          </Field>
        </div>
      </Modal>
    </div>
  );
}

// ===========================================================================

function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    void api
      .get<{ entries: AuditEntry[] }>(`/admin/audit?limit=200${filter ? `&action=${filter}` : ''}`)
      .then((d) => setEntries(d.entries))
      .finally(() => setLoading(false));
  }, [filter]);

  return (
    <Card
      title="Audit log"
      action={
        <Select value={filter} onChange={(e) => setFilter(e.target.value)} className="w-44">
          <option value="">All actions</option>
          <option value="auth">Authentication</option>
          <option value="instance">Instances</option>
          <option value="user">Users</option>
          <option value="role">Roles</option>
          <option value="group">Groups</option>
        </Select>
      }
    >
      {loading ? (
        <Spinner />
      ) : entries.length === 0 ? (
        <Empty title="Nothing recorded yet" />
      ) : (
        <div className="max-h-[600px] overflow-y-auto">
          <table className="w-full text-xs">
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-grid last:border-0">
                  <td className="w-24 px-4 py-2 text-ink-muted">
                    {formatRelative(e.at)}
                  </td>
                  <td className="w-8 px-1 py-2">
                    <span
                      className="inline-block h-1.5 w-1.5 rounded-full"
                      style={{
                        background: e.result === 'success' ? 'var(--color-good)' : 'var(--color-critical)',
                      }}
                      title={e.result}
                    />
                  </td>
                  <td className="w-40 px-2 py-2 font-mono">{e.action}</td>
                  <td className="hidden w-48 px-2 py-2 text-ink-secondary sm:table-cell">
                    {e.user_email ?? 'system'}
                  </td>
                  <td className="px-2 py-2 text-ink-muted">
                    {e.target_id && <span className="font-mono">{e.target_id}</span>}
                    {Object.keys(e.detail).length > 0 && (
                      <span className="ml-2 opacity-70">
                        {Object.entries(e.detail)
                          .slice(0, 3)
                          .map(([k, v]) => `${k}=${String(v)}`)
                          .join(' ')}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function Tag({ children, tone }: { children: React.ReactNode; tone?: 'good' | 'warning' | 'critical' }) {
  const colours = {
    good: 'var(--color-good)',
    warning: 'var(--color-warning)',
    critical: 'var(--color-critical)',
  };

  return (
    <span
      className="rounded px-1.5 py-0.5 text-[10px]"
      style={{
        background: tone
          ? `color-mix(in srgb, ${colours[tone]} 18%, transparent)`
          : 'var(--color-plane)',
        color: tone ? colours[tone] : 'var(--color-ink-muted)',
      }}
    >
      {children}
    </span>
  );
}
